import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/GeminiChatHistory.graphql';
import { Query, QuerygeminiChatHistoryArgs } from '../../__generated__/globalTypes';

export const useGeminiChatHistoryQuery = (options?: QueryHookOptions<Query, QuerygeminiChatHistoryArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerygeminiChatHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useGeminiChatHistoryLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerygeminiChatHistoryArgs>
): QueryResult<Query, QuerygeminiChatHistoryArgs> & {
  getGeminiChatHistory: LazyQueryExecFunction<Query, QuerygeminiChatHistoryArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getGeminiChatHistory, result] = useLazyQuery<Query, QuerygeminiChatHistoryArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getGeminiChatHistory, ...result };
};

export const useGeminiChatHistoryPromiseQuery = () => {
  const client = useApolloClient();

  const getGeminiChatHistory = useCallback(
    (options?: Omit<QueryOptions<QuerygeminiChatHistoryArgs, Query>, 'query'>) =>
      client.query<Query, QuerygeminiChatHistoryArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getGeminiChatHistory };
};
// Suspense Query Hooks
export const useGeminiChatHistorySuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerygeminiChatHistoryArgs>
) => {
  const result = useSuspenseQuery<Query, QuerygeminiChatHistoryArgs>(QUERY, {
    ...options
  });

  return result;
};
